/**
 * storeBilling — chooses the BillingProvider the store runs on, and exposes
 * the account hooks the sync layer calls.
 *
 *   STORE_BILLING_LIVE = false  →  MockBillingProvider (no charges; every
 *                                   purchase surface is a waitlist link
 *                                   anyway — launch.ts)
 *   STORE_BILLING_LIVE = true   →  native shell:  RevenueCatBillingProvider
 *                                   web:           WebBillingUnavailableProvider
 *                                   (the Capacitor plugin has no web
 *                                   implementation, and the mock must NEVER
 *                                   face the public: it hands out premium
 *                                   for free)
 *
 * The choice is deferred behind `LazyBillingProvider` because the store
 * factory is synchronous and the platform check + plugin load are not.
 *
 * INVARIANT (SEAM_REGISTRY seam 6): this module never touches store state.
 * Every Subscription that leaves it goes to the store through an action
 * that applies `preserveGrant()`. `onRemoteUpdate` is that action.
 */

import type { BillingProvider, Subscription } from '../types';
import type { StorePrices } from './prices';
import { STORE_BILLING_LIVE } from './launch';
import { MockBillingProvider } from './mockBilling';
import { LazyBillingProvider } from './lazyBilling';

let active: LazyBillingProvider | MockBillingProvider | null = null;

export function resolveBilling(
  getState: () => Subscription,
  onRemoteUpdate: (incoming: Subscription) => void,
  onStep?: (line: string) => void,
): BillingProvider {
  if (!STORE_BILLING_LIVE) {
    active = new MockBillingProvider(getState);
    return active;
  }
  const lazy = new LazyBillingProvider(async () => {
    const { isNative, isIOS } = await import('../native/native');
    if (!isNative()) {
      const { WebBillingUnavailableProvider } = await import('./revenueCatBilling');
      return new WebBillingUnavailableProvider();
    }
    const [{ RevenueCatBillingProvider }, { lazyPurchases, revenueCatKeyForPlatform }] =
      await Promise.all([import('./revenueCatBilling'), import('./revenueCatPlugin')]);
    return new RevenueCatBillingProvider(lazyPurchases, getState, {
      apiKey: revenueCatKeyForPlatform(isIOS() ? 'ios' : 'android'),
      onRemoteUpdate,
      onStep,
    });
  });
  active = lazy;
  // Boot-time entitlement read — off the synchronous store-creation path.
  // Null means "nothing to say" (web, no key, offline); no state change.
  setTimeout(() => {
    void lazy.refresh().then((sub) => {
      if (sub) onRemoteUpdate(sub);
    });
  }, 0);
  return lazy;
}

/**
 * Called by the auth listener on sign-in. Returns the entitlement the
 * account holds so the caller can apply it (through preserveGrant), or null
 * when billing is not live / not native / not configured.
 */
export async function identifyBillingAccount(uid: string): Promise<Subscription | null> {
  if (!active || !(active instanceof LazyBillingProvider)) return null;
  return active.identify(uid);
}

/**
 * Settings → "Run billing self-test": configure + fetch offerings, every step
 * narrated through onStep, no purchase. Null when billing is not live.
 */
export async function billingSelfTest(): Promise<string[] | null> {
  if (!active || !(active instanceof LazyBillingProvider)) return null;
  return active.selfTest();
}

/** Paywall open (native, live): store price labels, {} otherwise. Never throws. */
export async function billingStorePrices(): Promise<StorePrices> {
  if (!active || !(active instanceof LazyBillingProvider)) return {};
  try {
    return await active.storePrices();
  } catch {
    return {};
  }
}

/** Called by the auth listener on sign-out. Never throws. */
export async function forgetBillingAccount(): Promise<void> {
  if (!active || !(active instanceof LazyBillingProvider)) return;
  await active.forget();
}
